import {
  Dialog,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { useContractsStatus } from "../../hooks/useContractsStatus";
import { fetchData } from "../../api/apiHelpers";
import {
  athenaDarkPaperSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaBackdropSx,
} from "../layout/AthenaDialogStyles";
import { athenaColors } from "../../theme/palette";
import WizardStepIntro from "./wizard/WizardStepIntro";
import WizardStepAgreement from "./wizard/WizardStepAgreement";
import WizardStepConsent from "./wizard/WizardStepConsent";
import WizardStepComplete from "./wizard/WizardStepComplete";

export interface ContractsWizardModalProps {
  open: boolean;
  onClose: () => void;
  initialStep?: number;
}

type OrgResponse = { name?: string };

export default function ContractsWizardModal({
  open,
  onClose,
  initialStep,
}: ContractsWizardModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const status = useContractsStatus();
  const [currentStep, setCurrentStep] = useState(0);
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string>("");
  const [agreementModules, setAgreementModules] = useState<string[]>(["core", "module_b"]);

  const steps = [
    t("contracts_wizard.step.intro", "Introduction"),
    t("contracts_wizard.step.agreement", "Agreement"),
    t("contracts_wizard.step.consent", "Patient Consent"),
    t("contracts_wizard.step.complete", "Complete"),
  ];

  const resolveInitialStep = useCallback((): number => {
    if (typeof initialStep === "number" && initialStep >= 0 && initialStep <= 3) {
      return initialStep;
    }
    if (status.agreementStatus === "signed" && status.consentAcknowledged) {
      return 3;
    }
    if (status.agreementStatus === "signed" && !status.consentAcknowledged) {
      return 2;
    }
    if (
      status.agreementStatus === "draft" ||
      status.agreementStatus === "pending_review" ||
      (status.agreementId && status.agreementStatus !== "not_started")
    ) {
      return 1;
    }
    return 0;
  }, [initialStep, status.agreementStatus, status.consentAcknowledged, status.agreementId]);

  useEffect(() => {
    if (open) {
      setCurrentStep(resolveInitialStep());
      setAgreementId(status.agreementId);
    }
  }, [open, resolveInitialStep, status.agreementId]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const org = await fetchData<OrgResponse>("/billing/organization/");
        if (mounted && org?.name) setOrgName(org.name);
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open]);

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleIntroContinue = () => setCurrentStep(1);
  const handleAgreementSigned = (modules: string[]) => {
    setAgreementModules(modules);
    status.refresh();
    setCurrentStep(2);
  };
  const handleAgreementCreated = (id: string) => {
    setAgreementId(id);
  };
  const handleConsentComplete = () => {
    status.refresh();
    setCurrentStep(3);
  };
  const handleCompleteClose = () => onClose();

  const stepperSx = {
    "& .MuiStepLabel-root": { cursor: "pointer" },
    "& .MuiStepLabel-label": { color: alpha(theme.palette.common.white, 0.7) },
    "& .MuiStepLabel-label.Mui-active": { color: athenaColors.periwinkle, fontWeight: 600 },
    "& .MuiStepLabel-label.Mui-completed": { color: alpha(theme.palette.common.white, 0.9) },
    "& .MuiStepIcon-root": { color: alpha(theme.palette.common.white, 0.4) },
    "& .MuiStepIcon-root.Mui-active": { color: athenaColors.periwinkle },
    "& .MuiStepIcon-root.Mui-completed": { color: athenaColors.periwinkle },
    "& .MuiStepConnector-line": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: athenaDarkPaperSx }}
      slotProps={{ backdrop: { sx: athenaBackdropSx } }}
    >
      <DialogTitle sx={athenaDarkTitleSx}>
        <ShieldIcon sx={{ fontSize: 28, opacity: 0.9 }} />
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {t("contracts_wizard.title", "Legal Setup")}
        </Typography>
      </DialogTitle>

      <Stepper
        activeStep={currentStep}
        sx={{ px: 3, pt: 1, pb: 0, ...stepperSx }}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={index < currentStep}>
            <StepLabel
              onClick={() => handleStepClick(index)}
              sx={{ "& .MuiStepLabel-labelContainer": { cursor: index < currentStep ? "pointer" : "default" } }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <DialogContent sx={athenaDarkContentSx}>
        {currentStep === 0 && (
          <WizardStepIntro onContinue={handleIntroContinue} />
        )}
        {currentStep === 1 && (
          <WizardStepAgreement
            agreementId={agreementId}
            onAgreementCreated={handleAgreementCreated}
            onSigned={handleAgreementSigned}
            onBack={() => setCurrentStep(0)}
            orgName={orgName}
          />
        )}
        {currentStep === 2 && (
          <WizardStepConsent
            onComplete={handleConsentComplete}
            onBack={() => setCurrentStep(1)}
            agreementModules={agreementModules}
          />
        )}
        {currentStep === 3 && (
          <WizardStepComplete onClose={handleCompleteClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}
